import {FormControl, FormField, TextInput, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Icon, Text, VStack} from 'platform/foundation';

import {useController, UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {RecordAuditDefinitionFormValue} from '../types/RecordAuditDefinitionFormValue';
import {ResourcesField} from './ResourcesField';

export interface GeneralSectionProps extends TestIdProps {
  control: FormControl<RecordAuditDefinitionFormValue>;
  formApi: UseFormReturn<RecordAuditDefinitionFormValue>;
  totalWeight: number;
}

export function GeneralSection(props: GeneralSectionProps) {
  const thresholdController = useController({
    control: props.formApi.control,
    name: 'successThreshold',
  });

  const threshold = thresholdController.field.value;
  // Display math clamps to 0–100, typing is never blocked. The field value is null
  // while the input is cleared (required error surfaces on submit via the schema).
  const clampedThreshold = Math.max(0, Math.min(100, threshold ?? 0));
  const requiredPoints = Math.ceil((clampedThreshold / 100) * props.totalWeight);

  const handleThresholdChange = (value: string | null) => {
    const digits = (value ?? '').replace(/[^0-9]/g, '');
    thresholdController.field.onChange(digits === '' ? null : Number(digits));
  };

  return (
    <VStack spacing={3} data-testid={suffixTestId('generalSection', props)}>
      <Heading size={4}>{i18n.t('general.labels.general')}</Heading>
      <FormField
        type="text"
        control={props.control}
        name="name"
        label={i18n.t('general.labels.name')}
        placeholder={i18n.t('entity.recordAudit.checklist.namePlaceholder')}
        maxLength={255}
        data-testid={suffixTestId('name', props)}
      />
      <FormField
        type="textarea"
        control={props.control}
        name="description"
        label={i18n.t('general.labels.description')}
        placeholder={i18n.t('entity.recordAudit.checklist.descriptionPlaceholder')}
        minRows={3}
        maxLength={255}
        data-testid={suffixTestId('description', props)}
      />
      <HStack spacing={4} align="flex-start">
        <Box flex={1}>
          <FormField
            type="text"
            control={props.control}
            name="category"
            label={i18n.t('entity.recordAudit.checklist.category')}
            placeholder={i18n.t('entity.recordAudit.checklist.categoryPlaceholder')}
            maxLength={255}
            data-testid={suffixTestId('category', props)}
          />
        </Box>
        <Box width={80}>
          <VStack spacing={1}>
            <HStack spacing={1} align="center">
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.recordAudit.checklist.successThreshold')}
              </Text>
              <Tooltip label={i18n.t('page.settings.generalChecklist.successThresholdTooltip')}>
                <Icon value="action/info_outline" size={3} color="text.secondary" />
              </Tooltip>
            </HStack>
            <HStack spacing={3} align="center">
              <Box width={24}>
                <TextInput
                  value={isNil(threshold) ? null : threshold.toString()}
                  onChange={handleThresholdChange}
                  suffix="%"
                  isInvalid={isNotNil(thresholdController.fieldState.error)}
                  errorMessage={thresholdController.fieldState.error?.message}
                  data-testid={suffixTestId('successThreshold', props)}
                />
              </Box>
              {props.totalWeight > 0 ? (
                <Text size="small" color="secondary">
                  {i18n.t('page.settings.generalChecklist.thresholdPoints', {
                    required: requiredPoints,
                    total: props.totalWeight,
                  })}
                </Text>
              ) : (
                <Text size="small" color="tertiary">
                  {i18n.t('page.settings.generalChecklist.addTasksHint')}
                </Text>
              )}
            </HStack>
          </VStack>
        </Box>
      </HStack>
      <ResourcesField formApi={props.formApi} data-testid={suffixTestId('resources', props)} />
    </VStack>
  );
}
